import { Clock, Mic, MicOff, SkipForward } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FadeLoader } from 'react-spinners';

import demoAvatar from '../../assets/images/demoAvatar.png';
import { useAppSelector } from '../../redux/store/hooks';

import CountdownTimer from './CountDownTimer';

const Interview = ({
  currentQuestion,
  currentQuestionIndex,
  isRecording,
  isPlayingAudio,
  showTimer,
  audioRef,
  handleTimerComplete,
  handleNextQuestion,
  overallTimer,
  setOverallTimer,
  loading
}: {
  currentQuestion: any;
  currentQuestionIndex: number;
  isRecording: boolean;
  isPlayingAudio: boolean;
  showTimer: boolean;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  handleTimerComplete: () => void;
  handleNextQuestion: () => void;
  overallTimer: number;
  setOverallTimer: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
}) => {
  const {
    testData,
    avatars,
    interviewCompanyNamesIds,
    selectedInterviewCompany
  } = useAppSelector(state => state.root.interview);

  const getAvatarImage = () => {
    const avatar = avatars.find(
      avatar =>
        avatar.id === testData?.testSet?.questions[currentQuestionIndex]?.voice
    );
    return avatar?.avatarUrl ?? '';
  };

  const getOverallTimeColor = () => {
    if (overallTimer > 600) return 'text-green-600';
    if (overallTimer > 300) return 'text-yellow-600';
    return 'text-red-600';
  };

  useEffect(() => {
    if (overallTimer <= 0) {
      handleTimerComplete();
      return;
    }

    if (!isPlayingAudio && isRecording) {
      const timer = setInterval(() => {
        setOverallTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [
    handleTimerComplete,
    overallTimer,
    setOverallTimer,
    isPlayingAudio,
    isRecording
  ]);

  const minutes = Math.floor(overallTimer / 60);
  const remainingSeconds = overallTimer % 60;

  let statusMessage: JSX.Element;

  if (isPlayingAudio) {
    statusMessage = (
      <p className='font-medium text-blue-600'>Playing question...</p>
    );
  } else if (showTimer) {
    statusMessage = (
      <p className='mt-3 inline-block rounded-md px-3 py-1 text-2xl font-medium text-green-600'>
        <span className='shimmer-text'>Your turn to speak</span>
      </p>
    );
  } else {
    statusMessage = <p className='text-gray-600'>Loading question...</p>;
  }
  const getCompanyNames = (currentQuestion: any) => {
    const currentCompanyTags = currentQuestion?.companyTags;
    let matchedCompany = '';
    currentCompanyTags?.map((eachTagId: string) => {
      interviewCompanyNamesIds.forEach(eachCompany => {
        if (eachCompany.id === eachTagId) {
          matchedCompany = matchedCompany
            ? `${matchedCompany}, ${eachCompany.companyName}`
            : `${eachCompany.companyName}`;
        }
      });
    });
    if (matchedCompany === '') {
      return selectedInterviewCompany?.companyName;
    } else {
      return matchedCompany;
    }
  };

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraEnabled(true);
      } catch (error) {
        console.error('Error accessing camera:', error);
        setCameraEnabled(false);
      }
    };

    initCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {loading && (
        <div
          style={{ height: 'calc(-5rem + 100vh)' }}
          className='flex flex-col items-center justify-center p-8 text-center'
        >
          <h2 className='mb-2 text-xl font-semibold text-gray-800'>
            Saving Your Test...
          </h2>
          <p className='mb-6 max-w-md text-gray-600'>
            Please wait while your test is being saved. Do not refresh the page
            or perform any other action.
          </p>
          <FadeLoader color={'#36d7b7'} loading={loading} />
        </div>
      )}

      {!loading && (
        <div className='mx-auto h-full p-6'>
          <div className='w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl'>
            <div className='bg-slate-900 text-white'>
              <div className='hidden items-center justify-between px-6 py-4 md:flex'>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center gap-3 text-sm'>
                    {isRecording ? (
                      <span className='flex items-center gap-2 rounded-2xl bg-red-500/40 px-3 py-1 text-sm font-medium text-red-600'>
                        <span className='h-2 w-2 animate-pulse rounded-full bg-red-600' />
                        REC
                      </span>
                    ) : (
                      <span className='flex items-center gap-2 text-gray-400'>
                        <MicOff className='h-4 w-4' />
                        Standby
                      </span>
                    )}
                  </div>
                  <span className='text-sm font-medium'>
                    {getCompanyNames(currentQuestion)}
                  </span>
                </div>

                <div
                  className={`flex items-center gap-2 font-semibold ${getOverallTimeColor()}`}
                >
                  <Clock className='h-4 w-4' />
                  {minutes}:{remainingSeconds.toString().padStart(2, '0')}
                </div>

                <button
                  onClick={handleNextQuestion}
                  disabled={!isRecording}
                  className={`flex max-w-40 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition
          ${
            isRecording
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'cursor-not-allowed bg-gray-600'
          }`}
                >
                  {testData?.testSet?.questions?.length - 1 ===
                  currentQuestionIndex ? (
                    'Submit Answer'
                  ) : (
                    <>
                      Next Question
                      <SkipForward className='h-4 w-4' />
                    </>
                  )}
                </button>
              </div>

              {/* Mobile Layout */}
              <div className='flex flex-col md:hidden'>
                <div className='mt-2 flex items-center justify-between px-4 py-3'>
                  <div className='flex w-full items-center justify-between gap-1'>
                    {isRecording ? (
                      <span className='flex items-center gap-2 rounded-2xl bg-red-500/40 px-3 py-1 text-sm font-medium text-red-600'>
                        <span className='h-2 w-2 animate-pulse rounded-full bg-red-600' />
                        REC
                      </span>
                    ) : (
                      <span className='flex items-center gap-2 text-sm text-gray-400'>
                        <MicOff className='h-4 w-4' />
                        Standby
                      </span>
                    )}
                    <span className='text-sm font-medium'>
                      {getCompanyNames(currentQuestion)}
                    </span>
                  </div>
                </div>

                <div className='flex items-center justify-between gap-3 px-4 pb-3'>
                  <div
                    className={`flex items-center gap-2 font-semibold ${getOverallTimeColor()}`}
                  >
                    <Clock className='h-4 w-4' />
                    {minutes}:{remainingSeconds.toString().padStart(2, '0')}
                  </div>

                  <button
                    onClick={handleNextQuestion}
                    disabled={!isRecording}
                    className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition
            ${
              isRecording
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'cursor-not-allowed bg-gray-600'
            }`}
                  >
                    {testData?.testSet?.questions?.length - 1 ===
                    currentQuestionIndex ? (
                      'Submit Answer'
                    ) : (
                      <>
                        Next Question
                        <SkipForward className='h-4 w-4' />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-6 p-6 md:grid-cols-3'>
              <div className='md:col-span-2'>
                <div className='relative rounded-2xl bg-gradient-to-br from-rose-100 to-rose-200 shadow-lg'>
                  <div className='flex flex-col items-center'>
                    <img
                      src={getAvatarImage() || demoAvatar}
                      alt='Interviewer Avatar'
                      className='mx-auto h-52 w-full rounded-2xl shadow-md md:h-96'
                    />
                  </div>
                </div>
                <div className='my-5'>
                  <p className='font-bold'>
                    Question {currentQuestionIndex + 1}
                    {' of '}
                    {testData?.testSet?.questions?.length}
                  </p>
                  <p className='my-3'>{currentQuestion?.transcript}</p>
                </div>
              </div>

              <div className='flex flex-col items-center justify-between rounded-2xl bg-slate-900 p-6 text-white shadow-lg'>
                <div className='relative h-56 w-full overflow-hidden rounded-xl bg-slate-800'>
                  {cameraEnabled ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className='h-full w-full object-cover'
                    />
                  ) : (
                    <div className='flex h-full w-full items-center justify-center'>
                      <span className='text-sm text-gray-400'>Camera Off</span>
                    </div>
                  )}
                </div>

                <div className='mt-6'>
                  {showTimer ? (
                    <CountdownTimer
                      seconds={120}
                      onComplete={handleTimerComplete}
                    />
                  ) : (
                    <div className='flex flex-col items-center gap-3 text-gray-400'>
                      <Mic className='h-10 w-10' />
                      <p className='text-sm'>
                        {isPlayingAudio
                          ? 'Listen carefully...'
                          : 'Get ready to answer'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <audio ref={audioRef} preload='auto' className='hidden' />
          </div>
        </div>
      )}
    </>
  );
};

export default Interview;
